import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { applyGroupFilter } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { IGroup } from "@formant/core/model/IGroup";
import { ITagTemplate } from "@formant/core/model/ITagTemplate";

import { track } from "../../analytics";
import {
    appStore,
    authStore,
    channelStore,
    deviceStore,
    eventStore,
    groupStore,
    routerStore,
    tagTemplateStore,
} from "../../stores";
import { EventsButton } from "../common/EventsButton";
import { Help } from "../common/Help";
import { NoDataMessage } from "../common/NoDataMessage";
import { RestrictedComponent } from "../common/RestrictedComponent";
import { AddButton } from "./AddButton";
import * as styles from "./ChannelList.scss";
import DeviceList from "./DeviceList";
import { LinkList } from "./LinkList";
import { LinkListItem } from "./LinkListItem";
import { ListHeader } from "./ListHeader";
import { Section } from "./Section";

export interface IChannelListProps {
    onClose: () => void;
}

@observer
export class ChannelList extends Component<IChannelListProps> {
    public render() {
        const { onClose } = this.props;
        const isKiosk = authStore.hasAccess("kiosk");
        const { onDeleteDevice } = this;
        const { routeTo } = routerStore;
        const hideChannels = false;

        const hideRobotList = false;

        const channels = channelStore.list();
        const groupTagTemplates = tagTemplateStore.list();
        const loading =
            groupTagTemplates === undefined || channels === undefined;

        const channelItems = (channels || []).map(channel => {
            const id = defined(channel.id);

            const channelEventFilter = {
                ...channel.filter,
                viewed: false,
                disableNullMatches: true,
                notificationEnabled: true,
            };

            return (
                <LinkListItem
                    key={id}
                    icon="channel"
                    title={channel.name}
                    to={routeTo.channel({ id })}
                    onClicked={onClose}
                    className={
                        eventStore.hasNotifications(channelEventFilter) &&
                        styles["has-notifications"]
                    }
                    buttonClassName="channel"
                    data-testid={`sidebar-channel-${id}`}
                >
                    <EventsButton
                        filter={channelEventFilter}
                        to={routeTo.channelEvents({ id })}
                        small={true}
                    />
                </LinkListItem>
            );
        });

        const groupItems = (groupTagTemplates || [])
            .map<[ITagTemplate, IGroup[]]>(groupTagTemplate => [
                groupTagTemplate,
                groupStore
                    .list()
                    ?.filter(
                        _ => _.tagKey === groupTagTemplate.tagKey && _.active,
                    ) || [],
            ])
            .filter(
                ([tagTemplate, groups]) =>
                    tagTemplate.isGroup && groups.length > 0,
            )
            .map(([groupTagTemplate, groups], index) => (
                <div key={index}>
                    <ListHeader
                        title={groupTagTemplate.tagKey}
                        data-testid={`group-key-${groupTagTemplate.tagKey}`}
                    />
                    <LinkList>
                        {groups.map(group => {
                            const id = defined(group.id);
                            return (
                                <LinkListItem
                                    data-testid={`sidebar-group-${groupTagTemplate.tagKey}-${group.name}`}
                                    key={id}
                                    icon="group"
                                    title={group.name}
                                    to={routeTo.smartFleet({
                                        id,
                                    })}
                                    onClicked={onClose}
                                    className={classnames(
                                        eventStore.hasNotifications({
                                            ...applyGroupFilter(group),
                                            viewed: false,
                                            notificationEnabled: true,
                                        }) && styles["has-notifications"],
                                    )}
                                />
                            );
                        })}
                    </LinkList>
                </div>
            ));

        return (
            <Section loading={loading} className={styles["channel-list"]}>
                <div className={styles.content}>
                    {!isKiosk && !hideChannels && (
                        <>
                            <ListHeader title="Channels">
                                <RestrictedComponent
                                    resource="channels"
                                    requiredAccess="administrator"
                                >
                                    <AddButton
                                        to={routeTo.createChannel()}
                                        onClicked={onClose}
                                        data-tooltip="Create new channel"
                                        data-testid="create-new-channel-button"
                                    />
                                </RestrictedComponent>
                            </ListHeader>
                            <LinkList>{channelItems}</LinkList>
                            {channelItems.length === 0 && (
                                <NoDataMessage>
                                    No channels configured
                                </NoDataMessage>
                            )}
                        </>
                    )}
                    {!isKiosk && !hideRobotList && (
                        <>
                            <ListHeader title="Devices">
                                <>
                                    <RestrictedComponent
                                        resource="devices"
                                        requiredAccess="administrator"
                                    >
                                        <AddButton
                                            to={routeTo.createDevice()}
                                            onClicked={onClose}
                                            data-tooltip="Provision a new device"
                                            data-testid="provision-device-button"
                                        />
                                    </RestrictedComponent>
                                </>
                            </ListHeader>
                            <DeviceList
                                onClose={onClose}
                                onDeleteDevice={onDeleteDevice}
                            />
                        </>
                    )}
                    {groupItems}
                </div>
            </Section>
        );
    }

    onDeleteDevice = (id: string) => {
        const { navigate, routeTo } = routerStore;
        track("Device deletion started");
        const titleHelp = (
            <Help
                text="Formant Docs: Delete, disable or uninstall agent"
                to="https://docs.formant.io/docs/managing-a-native-installation#uninstalling-the-formant-agent"
            />
        );
        appStore.setConfirm({
            title: "Delete device",
            message: "Are you sure you want to delete this device?",
            okCopy: "Delete",
            titleHelp,
            onOk: async () => {
                track("Device deleted");
                await deviceStore.disable(id);
                appStore.setSnackbar("Device deleted");
                await navigate(routeTo.home());
            },
            critical: true,
        });
    };
}
